/**
 * Budget de débit d'ENVOI en maillage WebRTC — pur, testé sans WebRTC réel.
 *
 * En maillage chaque participant envoie sa vidéo à CHACUN des autres : le débit
 * montant total croît avec le nombre de pairs (à 7 pairs × 2,5 Mbps il faudrait
 * ~17 Mbps montants, hors de portée d'une box ADSL ou d'un téléphone). On répartit
 * donc un budget total borné entre les pairs, et au-delà de quelques pairs on
 * réduit aussi la résolution encodée (moins de CPU).
 *
 * Le partage d'écran est PRIORITAIRE (texte à lire) : il reçoit une part plus
 * généreuse du budget, et n'est jamais réduit en résolution.
 */

import { CAMERA_MAX_BITRATE_BPS, SCREEN_SHARE_MAX_BITRATE_BPS } from './media-quality';

/** Débit montant total visé, tous pairs confondus. */
export const TOTAL_UPLOAD_BUDGET_BPS = 6_000_000;

/** Plancher : en dessous, la vidéo devient inexploitable — mieux vaut la couper. */
export const MIN_CAMERA_BITRATE_BPS = 150_000;
export const MIN_SCREEN_SHARE_BITRATE_BPS = 400_000;

/** Le partage d'écran reçoit 25 % de plus que la caméra par pair (netteté du texte). */
export const SCREEN_SHARE_PRIORITY_FACTOR = 1.25;

export interface SendBudget {
  /** Plafond de débit par pair pour la piste caméra (bit/s). */
  cameraBps: number;
  /** Plafond de débit par pair pour la piste de partage d'écran (bit/s). */
  screenBps: number;
  /** Facteur de réduction de résolution de la caméra (1 = pleine résolution). */
  cameraScaleDown: 1 | 1.5 | 2;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** `peerCount` = nombre de pairs vers lesquels on envoie (0 → traité comme 1). */
export function computeSendBudget(peerCount: number): SendBudget {
  const peers = Math.max(1, Math.floor(Number.isFinite(peerCount) ? peerCount : 1));
  const perPeer = TOTAL_UPLOAD_BUDGET_BPS / peers;

  return {
    cameraBps: Math.round(clamp(perPeer, MIN_CAMERA_BITRATE_BPS, CAMERA_MAX_BITRATE_BPS)),
    screenBps: Math.round(
      clamp(
        perPeer * SCREEN_SHARE_PRIORITY_FACTOR,
        MIN_SCREEN_SHARE_BITRATE_BPS,
        SCREEN_SHARE_MAX_BITRATE_BPS,
      ),
    ),
    cameraScaleDown: peers >= 6 ? 2 : peers >= 4 ? 1.5 : 1,
  };
}
